/**
 * useTelehealthConsent — TV-P4-02 + TV-P4-03
 *
 * Manages the patient's FHIR Consent for telehealth:
 *
 *   load   — GET Consent?patient=<id>&category=<system|telehealth>&status=active
 *   record — POST Consent resource keyed by patient + category, with the
 *            patient's typed name in `verification` for audit.
 *
 * Category coding follows the CK-020 pattern (system + code + display)
 * but uses a CollabKare-coined system for the `telehealth` value since
 * the standard FHIR consentcategorycodes valueset has no telehealth
 * entry. Provider apps can search by either the system+code pair or
 * filter the Bundle client-side.
 *
 * Returns:
 *   {
 *     existingConsent: { id, dateTime, performer?, verification? } | null,
 *     isLoading:       boolean,
 *     error:           Error | null,
 *     recordConsent:   ({ typedName }) => Promise<resource>,
 *     isRecording:     boolean,
 *   }
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFHIR } from '@/fhir/hooks';

const CATEGORY_SYSTEM = 'https://collabkare.io/consent-category';
const CATEGORY_CODE = 'telehealth';
const CATEGORY_DISPLAY = 'Telehealth informed consent';

const POLICY_URI =
  'https://collabkare.com/legal/telehealth-consent/v1.0.0';

/**
 * Build the FHIR R4 Consent payload. Mirrors the shape sandman's
 * ConsentService.buildFhirConsentResource produces — same scope,
 * policyRule, and provision blocks — but with our custom category
 * coding and the patient's typed signature in `verification`.
 */
function buildConsentResource({ patientId, typedName }) {
  const now = new Date().toISOString();
  return {
    resourceType: 'Consent',
    status: 'active',
    scope: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
          code: 'treatment',
        },
      ],
    },
    category: [
      {
        coding: [
          { system: CATEGORY_SYSTEM, code: CATEGORY_CODE, display: CATEGORY_DISPLAY },
        ],
      },
    ],
    patient: { reference: `Patient/${patientId}` },
    dateTime: now,
    policy: [{ uri: POLICY_URI }],
    policyRule: {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: 'OPTIN',
        },
      ],
    },
    provision: {
      type: 'permit',
      period: { start: now },
    },
    // Typed signature lives in `verification`. R4 supports
    // verification[].verified=true with verificationDate + verifiedWith.
    verification: typedName
      ? [
          {
            verified: true,
            verificationDate: now,
            verifiedWith: { display: typedName },
          },
        ]
      : undefined,
  };
}

export function useTelehealthConsent(patientId) {
  const { client, isReady } = useFHIR();
  const queryClient = useQueryClient();

  const queryKey = ['Consent', 'telehealth', patientId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!client || !patientId) return null;
      // FHIR search: filter by patient + custom category coding + status.
      // HAPI accepts the category as `<system>|<code>`.
      const bundle = await client.search('Consent', {
        patient: patientId,
        category: `${CATEGORY_SYSTEM}|${CATEGORY_CODE}`,
        status: 'active',
        _sort: '-_lastUpdated',
        _count: 1,
      });
      const first = bundle?.entry?.[0]?.resource;
      return first || null;
    },
    enabled: isReady && !!patientId,
    staleTime: 5 * 60 * 1000,
  });

  const recordConsent = useMutation({
    mutationFn: async ({ typedName }) => {
      if (!isReady || !client) {
        throw new Error(
          'FHIR client is not ready — cannot record consent yet.',
        );
      }
      if (!patientId) {
        throw new Error('useTelehealthConsent: patientId required to record');
      }
      const resource = buildConsentResource({ patientId, typedName });
      const saved = await client.create(resource);
      if (!saved?.id) {
        throw new Error('FHIR server did not return a Consent on create.');
      }
      return saved;
    },
    onSuccess: (saved) => {
      // Push the new resource into the cache so the parent's
      // `existingConsent` flips green without a refetch round-trip.
      queryClient.setQueryData(queryKey, saved);
    },
  });

  return {
    existingConsent: query.data || null,
    isLoading: query.isLoading,
    error: query.error || recordConsent.error || null,
    recordConsent: (args) => recordConsent.mutateAsync(args),
    isRecording: recordConsent.isPending,
  };
}
